import { c as _c } from "react/compiler-runtime";
import { IcoCheckCircle16, IcoChevronRight16 } from '@onefootprint/icons';
import { LoadingSpinner } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { type ReactNode, useEffect, useState } from 'react';
import PinnedCard, { PinnedBody } from '../pinned-card';
export type TodoItemData = {
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
  activeForm?: string;
};
type Variant = 'pinned' | 'inline';
type Props = {
  todos: TodoItemData[];
  /**
   * `pinned` is the live view rendered above the composer while the agent is still
   * working (à la Manus): default expanded, framed in a DS card so it reads as a
   * persistent UI element rather than a chat bubble.
   *
   * `inline` is the archival view rendered at the original TodoWrite anchor once
   * every todo is complete: borderless, default collapsed (a single header line
   * users can re-expand to inspect the plan).
   */
  variant: Variant;
  /** When true, the pinned card starts collapsed regardless of progress. Used by
   *  chat-panel when a pinned question is also visible — keeps the stacked-cards
   *  area quiet so the question stays the primary focus. */
  startCollapsed?: boolean;
  /** Pinned variant only: a row rendered at the card's base, below a divider (the running-agents
   *  indicator, grouped under the plan). */
  footer?: ReactNode;
};
const TodoItem = t0 => {
  const $ = _c(11);
  const {
    todo
  } = t0;
  const completed = todo.status === "completed";
  const inProgress = todo.status === "in_progress";
  let t1;
  if ($[0] !== completed || $[1] !== inProgress) {
    t1 = <span className="shrink-0 mt-0.5 flex items-center justify-center w-4 h-4 transition-colors duration-200">{completed ? <IcoCheckCircle16 className="shrink-0 text-primary" /> : inProgress ? <LoadingSpinner size="default" color="accent" className="shrink-0" /> : <span className="block w-3.5 h-3.5 rounded-full border border-tertiary transition-colors duration-200" />}</span>;
    $[0] = completed;
    $[1] = inProgress;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = completed ? "text-tertiary line-through decoration-tertiary" : inProgress ? "text-primary" : "text-secondary";
  let t3;
  if ($[3] !== t2) {
    t3 = cx("transition-colors duration-200", t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = inProgress ? todo.activeForm ?? todo.content : todo.content;
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <span className={t3}>{t4}</span>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t1 || $[9] !== t5) {
    t6 = <li className="flex items-start gap-2.5 text-body-3">{t1}{t5}</li>;
    $[8] = t1;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
const PinnedTodoList = t0 => {
  const $ = _c(58);
  const {
    todos,
    variant,
    startCollapsed,
    footer
  } = t0;
  let t1;
  if ($[0] !== todos) {
    t1 = todos.filter(_temp);
    $[0] = todos;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const completedCount = t1.length;
  let t2;
  if ($[2] !== todos) {
    t2 = todos.find(_temp2);
    $[2] = todos;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const inProgress = t2;
  const allDone = todos.length > 0 && completedCount === todos.length;
  const [expanded, setExpanded] = useState(variant === "pinned" && !allDone && !startCollapsed);
  let t3;
  let t4;
  if ($[4] !== allDone || $[5] !== variant) {
    t3 = () => {
      if (variant === "pinned" && allDone) {
        setExpanded(false);
      }
    };
    t4 = [variant, allDone];
    $[4] = allDone;
    $[5] = variant;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  if (!todos.length) {
    return null;
  }
  let t5;
  if ($[8] !== allDone || $[9] !== inProgress) {
    t5 = allDone ? "All tasks complete" : inProgress ? <>Working on <span className="text-secondary font-medium">{inProgress.activeForm ?? inProgress.content}</span></> : "Tasks queued";
    $[8] = allDone;
    $[9] = inProgress;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const headerLabel = t5;
  if (variant === "pinned") {
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = () => setExpanded(_temp3);
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    let t7;
    if ($[12] !== allDone || $[13] !== headerLabel) {
      t7 = allDone ? <span className="text-secondary">{headerLabel}</span> : headerLabel;
      $[12] = allDone;
      $[13] = headerLabel;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] !== t7) {
      t8 = <span className="truncate min-w-0">{t7}</span>;
      $[15] = t7;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    let t9;
    if ($[17] !== completedCount || $[18] !== todos.length) {
      t9 = <span className="font-mono tabular-nums">{completedCount} / {todos.length}</span>;
      $[17] = completedCount;
      $[18] = todos.length;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    const t10 = expanded && "rotate-90";
    let t11;
    if ($[20] !== t10) {
      t11 = cx("shrink-0 text-quaternary transition-transform", t10);
      $[20] = t10;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    let t12;
    if ($[22] !== t11) {
      t12 = <IcoChevronRight16 className={t11} />;
      $[22] = t11;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    let t13;
    if ($[24] !== t12 || $[25] !== t9) {
      t13 = <span className="shrink-0 flex items-center gap-1.5">{t9}{t12}</span>;
      $[24] = t12;
      $[25] = t9;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    let t14;
    if ($[27] !== expanded || $[28] !== t13 || $[29] !== t8) {
      t14 = <button type="button" onClick={t6} className="group/todo flex flex-1 min-w-0 items-center justify-between gap-3 text-caption-1 text-tertiary cursor-pointer" aria-expanded={expanded}>{t8}{t13}</button>;
      $[27] = expanded;
      $[28] = t13;
      $[29] = t8;
      $[30] = t14;
    } else {
      t14 = $[30];
    }
    let t15;
    if ($[31] !== expanded || $[32] !== todos) {
      t15 = expanded && <PinnedBody as="ol" itemCount={todos.length} className="flex flex-col gap-1.5 mt-3">{todos.map(_temp4)}</PinnedBody>;
      $[31] = expanded;
      $[32] = todos;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    let t16;
    if ($[34] !== expanded || $[35] !== footer || $[36] !== t14 || $[37] !== t15) {
      t16 = <PinnedCard shrinkable={expanded} footer={footer} header={t14}>{t15}</PinnedCard>;
      $[34] = expanded;
      $[35] = footer;
      $[36] = t14;
      $[37] = t15;
      $[38] = t16;
    } else {
      t16 = $[38];
    }
    return t16;
  }
  let t6;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setExpanded(_temp5);
    $[39] = t6;
  } else {
    t6 = $[39];
  }
  let t7;
  if ($[40] !== completedCount || $[41] !== headerLabel || $[42] !== todos.length) {
    t7 = <span className="text-caption-1 text-tertiary group-hover/todo:text-secondary transition-colors truncate">{headerLabel} ({completedCount}/{todos.length})</span>;
    $[40] = completedCount;
    $[41] = headerLabel;
    $[42] = todos.length;
    $[43] = t7;
  } else {
    t7 = $[43];
  }
  const t8 = expanded && "rotate-90";
  let t9;
  if ($[44] !== t8) {
    t9 = cx("shrink-0 text-quaternary transition-transform", t8);
    $[44] = t8;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  let t10;
  if ($[46] !== t9) {
    t10 = <IcoChevronRight16 className={t9} />;
    $[46] = t9;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  let t11;
  if ($[48] !== expanded || $[49] !== t10 || $[50] !== t7) {
    t11 = <button type="button" onClick={t6} className="group/todo inline-flex items-center gap-1 self-start max-w-full text-left cursor-pointer" aria-expanded={expanded}>{t7}{t10}</button>;
    $[48] = expanded;
    $[49] = t10;
    $[50] = t7;
    $[51] = t11;
  } else {
    t11 = $[51];
  }
  let t12;
  if ($[52] !== expanded || $[53] !== todos) {
    t12 = expanded && <ol className="flex flex-col gap-1.5 mt-2">{todos.map(_temp6)}</ol>;
    $[52] = expanded;
    $[53] = todos;
    $[54] = t12;
  } else {
    t12 = $[54];
  }
  let t13;
  if ($[55] !== t11 || $[56] !== t12) {
    t13 = <div className="flex flex-col">{t11}{t12}</div>;
    $[55] = t11;
    $[56] = t12;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  return t13;
};
export default PinnedTodoList;
function _temp(t) {
  return t.status === "completed";
}
function _temp2(t_0) {
  return t_0.status === "in_progress";
}
function _temp3(v) {
  return !v;
}
function _temp4(todo) {
  return <TodoItem key={todo.content} todo={todo} />;
}
function _temp5(v_0) {
  return !v_0;
}
function _temp6(todo_0) {
  return <TodoItem key={todo_0.content} todo={todo_0} />;
}